/**
 * Smart Preloader for Route-Based Components
 * Preloads related screens based on user navigation patterns
 */

interface PreloadMap {
  [screen: string]: string[];
}

// Define which screens to preload based on current screen
const PRELOAD_MAP: PreloadMap = {
  // When on Home, preload most commonly accessed screens
  'Home': [
    'screen.shift-handover',
    'screen.observation', 
    'screen.pulse-check'
  ],
  
  // When on Shift Handover, preload related handover screens
  'Shift Handover': [
    'screen.shift-handover-redirect',
    'screen.leadership-walk'
  ],
  
  // When on Observations, preload quality and inspection screens
  'Observations': [
    'screen.quality-audit',
    'screen.racking-inspection',
    'screen.sparc-walk'
  ],
  
  // When on Leadership Walk, preload redirect and related screens
  'Leadership Walk': [
    'screen.leadership-walk-redirect',
    'screen.shift-handover'
  ],
  
  // When on New Hire Onboarding, preload touchpoint screens
  'NHO': [
    'screen.pulse-check-touchpoint',
    'screen.specific-period-touchpoint',
    'screen.manage-employees'
  ]
};

class SmartPreloader {
  private preloadedChunks: Set<string> = new Set();
  private preloadTimeout: NodeJS.Timeout | null = null;

  /**
   * Preload chunks for a given screen after a delay
   * @param currentScreen - The current screen name
   * @param delay - Delay in milliseconds before preloading (default: 2000ms)
   */
  preloadForScreen(currentScreen: string, delay: number = 2000): void {
    // Clear existing timeout
    if (this.preloadTimeout) {
      clearTimeout(this.preloadTimeout);
    }

    this.preloadTimeout = setTimeout(() => {
      this.executePreload(currentScreen);
    }, delay);
  }

  /**
   * Immediately preload chunks for a screen
   * @param currentScreen - The current screen name
   */
  private executePreload(currentScreen: string): void {
    const chunksToPreload = PRELOAD_MAP[currentScreen];
    
    if (!chunksToPreload) {
      return;
    }

    chunksToPreload.forEach(chunkName => {
      if (!this.preloadedChunks.has(chunkName)) {
        this.preloadChunk(chunkName);
        this.preloadedChunks.add(chunkName);
      }
    });
  }

  /**
   * Preload a specific webpack chunk
   * @param chunkName - The webpack chunk name to preload
   */
  private preloadChunk(chunkName: string): void {
    try {
      // For web platform, use webpack's preload functionality
      if (typeof window !== 'undefined' && 'webpackPreloadModule' in window) {
        // @ts-ignore - webpack runtime method
        window.webpackPreloadModule(chunkName);
      } else {
        // Fallback: create link preload element
        this.createPreloadLink(chunkName);
      }
    } catch (error) {
      console.debug(`SmartPreloader: Could not preload chunk ${chunkName}:`, error);
    }
  }

  /**
   * Create a preload link element for chunk
   * @param chunkName - The chunk name to preload
   */
  private createPreloadLink(chunkName: string): void {
    if (typeof document === 'undefined') return;

    // Try to find the actual chunk file name from webpack manifest
    const chunkFile = this.findChunkFile(chunkName);
    if (!chunkFile) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'script';
    link.href = chunkFile;
    link.crossOrigin = 'anonymous';
    
    // Add to head if not already present
    const existingLink = document.querySelector(`link[href="${chunkFile}"]`);
    if (!existingLink) {
      document.head.appendChild(link);
    }
  }

  /**
   * Find actual chunk file name from webpack manifest
   * @param chunkName - The chunk name to find
   */
  private findChunkFile(chunkName: string): string | null {
    try {
      // Access webpack's chunk mapping if available
      // @ts-ignore - webpack runtime
      if (window.__webpack_require__ && window.__webpack_require__.u) {
        // @ts-ignore - webpack runtime method
        return window.__webpack_require__.u(chunkName);
      }
    } catch (error) {
      console.debug(`SmartPreloader: Could not find chunk file for ${chunkName}`);
    }
    return null;
  }

  /**
   * Clear preload timeout
   */
  clear(): void {
    if (this.preloadTimeout) {
      clearTimeout(this.preloadTimeout);
      this.preloadTimeout = null;
    }
  }

  /**
   * Get preloaded chunks for debugging
   */
  getPreloadedChunks(): string[] {
    return Array.from(this.preloadedChunks);
  }
}

// Export singleton instance
export const smartPreloader = new SmartPreloader();

export default SmartPreloader;